// PublicHeader: Professional header for public pages with logo, dark/light mode, backend-linked logo
import React, { useContext, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { AppBar, Toolbar, Typography, IconButton, Box, Button, Drawer, List, ListItemButton, ListItemText, Divider } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ThemeToggle from './ThemeToggle';
import { Link } from 'react-router-dom';
import { SettingsContext } from '../context/SettingsContext';

function PublicHeader() {
  const settings = useContext(SettingsContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  const resolveAsset = (url, fallback) => {
    if (!url) return fallback;
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith('/')) return `${API_BASE}${url}`;
    return url;
  };
  const logoSrc = resolveAsset(settings?.logoUrl, `${API_BASE}/api/public/logo`);
  const platformName = settings?.platformName || 'Timte 2026';
  const pages = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Packages', path: '/packages' },
    { name: 'Schedule', path: '/schedule' },
    { name: 'Highlights', path: '/highlights' },
    { name: 'VIPs', path: '/vips' },
    { name: 'Venues', path: '/venues' },
    { name: 'Contact', path: '/contact' },
    { name: 'Privacy', path: '/privacy' },
  ];

  return (
    <AppBar position="fixed" color="inherit" elevation={3} sx={{ top: 0, left: 0, right: 0, width: '100%', borderRadius: 0, backdropFilter: 'saturate(120%) blur(6px)', zIndex: theme => theme.zIndex.appBar }}>
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', minHeight: { xs: 56, md: 56 }, py: { xs: 0.5, md: 1 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Link to="/">
            {settings ? (
              <img src={logoSrc} alt={platformName} style={{ height: 32, marginRight: 8, objectFit: 'contain' }} />
            ) : (
              <CircularProgress size={28} sx={{ mr: 1 }} />
            )}
          </Link>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              letterSpacing: 1,
              fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.25rem' },
              lineHeight: 1.1,
              maxWidth: { xs: 200, sm: 220, md: 400 },
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
            }}
          >
            {settings ? platformName : 'Loading...'}
          </Typography>
        </Box>
        {/* Desktop links */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
          {pages.map(page => (
            <Button
              key={page.name}
              component={Link}
              to={page.path}
              variant="text"
              sx={{
                color: 'text.primary',
                fontWeight: 600,
                fontSize: '1rem',
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                textTransform: 'none',
                bgcolor: 'transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(0,166,121,0.12)' : 'rgba(0,166,121,0.08)',
                  color: 'primary.main',
                },
              }}
            >
              {page.name}
            </Button>
          ))}
          <Box sx={{ ml: 2 }}>
            <ThemeToggle />
          </Box>
        </Box>

        {/* Mobile hamburger */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
          <IconButton aria-label="open menu" onClick={() => setMobileOpen(true)}>
            <MenuIcon />
          </IconButton>
        </Box>
      </Toolbar>
      {/* Mobile Drawer */}
      <Drawer anchor="left" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <Box sx={{ width: 260, p: 2, height: '100%', display: 'flex', flexDirection: 'column' }} role="presentation">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Link to="/" onClick={() => setMobileOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {settings ? (
                <img src={logoSrc} alt={platformName} style={{ height: 36, objectFit: 'contain' }} />
              ) : (
                <CircularProgress size={28} />
              )}
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{platformName}</Typography>
            </Link>
            <IconButton onClick={() => setMobileOpen(false)} aria-label="close menu"><CloseIcon /></IconButton>
          </Box>
          <Divider sx={{ mb: 1 }} />
          <List sx={{ flex: 1 }}>
            {pages.map(page => (
              <ListItemButton key={page.name} component={Link} to={page.path} onClick={() => setMobileOpen(false)}>
                <ListItemText primary={page.name} />
              </ListItemButton>
            ))}
          </List>
          <Divider />
          <Box sx={{ pt: 1 }}>
            <ThemeToggle />
          </Box>
        </Box>
      </Drawer>
    </AppBar>
  );
}

export default PublicHeader;
